import * as React from "react";
import {
  Grid,
  Typography,
  Link,
  Box,
  Switch,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Radio,
} from "@mui/material";
import { getHeaders } from "../../utils/HeaderHelper";
import { AuthenticationContext } from "../../Context";
import ResetPrivileges from "./ResetPrivileges";
import ClonePrivileges from "./ClonePrivileges";
import PrivilegesDefinition from "./PrivilegesDefinition";
import { PrivilegesContext } from "../../PrivilegesContext";
import { checkPrivileges } from "../../utils/PrivilegesHelper";

interface Props {
  selectedProvider: any;
  fetchUserPrivileges: any;
}

const PrivilegesEdit: React.FC<Props> = ({
  selectedProvider,
  fetchUserPrivileges,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const currentUser = authenticationSetting.currentUser;
  const userPrivileges = React.useContext(PrivilegesContext);
  const [privileges, setPrivileges] = React.useState<any>(null);
  const [globalPrivileges, setGlobalPrivileges] = React.useState<any>([]);
  const [providerRoles, setProviderRoles] = React.useState(null);
  const [error, setError] = React.useState<string>("");
  const [showConfirmResetModal, setShowConfirmResetModal] =
    React.useState<boolean>(false);
  const [showSelectCloneModal, setShowSelectCloneModal] =
    React.useState<boolean>(false);
  const [showCloneConfirmationModal, setShowCloneConfirmationModal] =
    React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [viewPrivilegesDefinitions, setViewPrivilegesDefinitions] =
    React.useState<boolean>(false);

  const [cloneableUsers, setCloneableUsers] = React.useState<any>(null);
  const [selectedCloneUserId, setSelectedCloneUserId] =
    React.useState<any>(null);
  const [selectedUserName, setSelectedUserName] = React.useState<string>("");

  const getPrivileges = () => {
    setLoading(true);
    fetch(`/data_fetching/get_user_privileges/${selectedProvider?.id}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setPrivileges(result.resource.privileges);
          setGlobalPrivileges(result.resource.global_privileges);
          setCloneableUsers(result.resource.cloneable_users);
          setProviderRoles(result.resource.provider_role);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  React.useEffect(() => {
    getPrivileges();
  }, [selectedProvider]);

  const updatePrivilegeState = (event) => {
    const privilege_id = event.target.value;
    const checked = event.target.checked;

    fetch(`/customer_user_privileges/${privilege_id}`, {
      method: "PUT",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        customer_user_privilege: {
          checked,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
        } else {
          getPrivileges();
          fetchUserPrivileges();
        }
      })
      .catch((error) => {
        setError(error);
      });
  };

  const updateProviderRole = (event) => {
    const selectedValue = event.target.value;

    let oAdmin = { id: orgAdmin.id, name: orgAdmin.name, state: false };
    let sAdmin = starfieldAdmin
      ? { id: starfieldAdmin.id, name: starfieldAdmin.name, state: false }
      : null;

    if (selectedValue === starfieldAdmin?.id?.toString()) {
      sAdmin.state = true;
    } else if (selectedValue === orgAdmin?.id?.toString()) {
      oAdmin.state = true;
    }

    const updatedProviderRoles: Record<string, any> = {};

    if (orgAdmin) {
      updatedProviderRoles.organization_admin = oAdmin;
    }

    if (starfieldAdmin && currentUser?.starfield_admin) {
      updatedProviderRoles.starfield_admin = sAdmin;
    }

    setLoading(true);

    fetch(`/update_provider_role`, {
      method: "PUT",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        provider_roles: updatedProviderRoles,
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
          setLoading(false);
        } else {
          setProviderRoles(result.resource.provider_role);
          fetchUserPrivileges();
          setLoading(false);
        }
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  };

  const resetPrivileges = () => {
    fetch(`/reset_customer_user_privileges/${selectedProvider?.user_id}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
        } else {
          closeShowConfirmResetModal();
          getPrivileges();
        }
      })
      .catch((error) => {
        setError(error);
      });
  };

  const clonePrivileges = () => {
    fetch(`/clone_customer_user_privileges/${selectedProvider?.user_id}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        customer_user_privilege: {
          clone_user_id: selectedCloneUserId,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
        } else {
          closeShowSelectCloneModal();
          closeShowCloneConfirmationModal();
          getPrivileges();
        }
      })
      .catch(() => {
        setError("Privileges cannot be cloned.");
      });
  };

  const handleResetClick = (event) => {
    event.preventDefault();
    openShowConfirmResetModal();
  };

  const handleCloneClick = (event) => {
    event.preventDefault();
    openShowSelectCloneModal();
  };

  const openShowConfirmResetModal = () => {
    setShowConfirmResetModal(true);
  };

  const closeShowConfirmResetModal = () => {
    setShowConfirmResetModal(false);
  };

  const openShowSelectCloneModal = () => {
    setShowSelectCloneModal(true);
  };

  const closeShowSelectCloneModal = () => {
    setShowSelectCloneModal(false);
  };

  const openShowCloneConfirmationModal = () => {
    setShowCloneConfirmationModal(true);
  };

  const closeShowCloneConfirmationModal = () => {
    setShowCloneConfirmationModal(false);
  };

  const openViewPrivilegesDefinitions = () =>
    setViewPrivilegesDefinitions(true);
  const closeViewPrivilegesDefinitions = () =>
    setViewPrivilegesDefinitions(false);

  const handleUserSelection = (user_id) => {
    setSelectedCloneUserId(user_id);
    const user = cloneableUsers.find((user) => user.id == user_id);
    setSelectedUserName(user.name_reversed);
  };

  const getActiveRole = (roles: any[] | null): string => {
    if (!roles) return "basic_user";

    const starfieldAdmin = roles.find(
      (role) => role.name === "Starfield Admin" && role.state
    );
    const orgAdmin = roles.find(
      (role) => role.name === "Organization Admin" && role.state
    );

    if (starfieldAdmin) return "starfield_admin";
    if (orgAdmin) return "org_admin";
    return "basic_user";
  };

  const orgAdmin = providerRoles?.find((p) => p.name === "Organization Admin");
  const starfieldAdmin = providerRoles?.find(
    (p) => p.name === "Starfield Admin"
  );

  const isLoggedInProvider =
    selectedProvider.user_id === currentUser.id &&
    selectedProvider.name === currentUser.name;

  return (
    <Grid item xs={12} className="info-container">
      
      <div className="overview-container row">
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "18px",
            letterSpacing: "1px",
            color: "#4A4442",
          }}
        >
          Provider Privileges
        </Typography>
      </div>

      <div className="divider" style={{ margin: "20px 0px" }}></div>

      {isLoggedInProvider ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {getActiveRole(providerRoles) === "starfield_admin" ? (
            <>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: "20px",
                  letterSpacing: "0.15px",
                  color: "black",
                }}
              >
                Starfield Admin
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "16px",
                  color: "black",
                  marginBottom: "16px",
                  marginTop: "2px",
                }}
              >
                You are a <b>Starfield Admin</b>. You can <b>view/modify</b>{" "}
                privileges for yourself and providers across all organizations.
              </Typography>
            </>
          ) : getActiveRole(providerRoles) === "org_admin" ? (
            <>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: "20px",
                  letterSpacing: "0.15px",
                  color: "black",
                }}
              >
                Organization Admin
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "16px",
                  color: "black",
                  marginBottom: "16px",
                  marginTop: "2px",
                }}
              >
                You are an <b>Organization Admin</b>. You can <b>view/modify</b>{" "}
                privileges for yourself and your colleagues.
              </Typography>
            </>
          ) : (
            <></>
          )}

          <div className="divider" style={{ margin: "0px" }}></div>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "12px",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "18px",
              letterSpacing: "1px",
              color: "#4A4442",
            }}
          >
            Select the role for this individual
          </Typography>

          <div className="row" style={{ columnGap: "32px" }}>
            <div className="row" style={{ columnGap: "5px" }}>
              <Radio
                checked={getActiveRole(providerRoles) === "basic_user"}
                value="basic_user"
                onChange={updateProviderRole}
                name="radio-buttons"
                inputProps={{ "aria-label": "basic_user" }}
                sx={{
                  color: "#212B36",
                  padding: "0px",
                  backgroundColor: "transparent",
                }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  color: "black",
                }}
              >
                Basic User
              </Typography>
            </div>
            {orgAdmin &&
              (checkPrivileges(userPrivileges, "Organization Admin") ||
                currentUser?.starfield_admin) && (
                <div className="row" style={{ columnGap: "5px" }}>
                  <Radio
                    checked={getActiveRole(providerRoles) === "org_admin"}
                    value={orgAdmin.id}
                    onChange={updateProviderRole}
                    name="radio-buttons"
                    inputProps={{ "aria-label": "org_admin" }}
                    sx={{
                      color: "#212B36",
                      padding: "0px",
                      backgroundColor: "transparent",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: "22px",
                      color: "black",
                    }}
                  >
                    {orgAdmin.name}
                  </Typography>
                </div>
              )}
            {starfieldAdmin && currentUser?.starfield_admin && (
              <div className="row" style={{ columnGap: "5px" }}>
                <Radio
                  checked={getActiveRole(providerRoles) === "starfield_admin"}
                  value={starfieldAdmin.id}
                  onChange={updateProviderRole}
                  name="radio-buttons"
                  inputProps={{ "aria-label": "starfield_admin" }}
                  sx={{
                    color: "#212B36",
                    padding: "0px",
                    backgroundColor: "transparent",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "22px",
                    color: "black",
                  }}
                >
                  {starfieldAdmin.name}
                </Typography>
              </div>
            )}
          </div>
          <div className="divider" style={{ margin: "0px" }}></div>
        </Box>
      )}

      <Grid item xs={12} className="content">
        <ul className="privilegesList" style={{ margin: "24px 0px 20px 0px" }}>
          {privileges?.map((privilege) => (
            <li
              key={privilege.id}
              style={{
                display: "flex",
                columnGap: "10px",
                alignItems: "center",
                marginLeft: "-9px",
                marginBottom: "4px",
              }}
            >
              <div className="privilegeToggle">
                <Switch
                  value={privilege.id}
                  checked={privilege.state}
                  onChange={updatePrivilegeState}
                  color="primary"
                />
              </div>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.15px",
                }}
              >
                {privilege.name}
              </Typography>
            </li>
          ))}
        </ul>
      </Grid>

      {currentUser.starfield_admin && (
        <>
          <div className="divider" style={{ margin: "0px" }}></div>

          <Grid item xs={12} className="content">
            <ul className="privilegesList" style={{ margin: "24px 0px" }}>
              {globalPrivileges?.map((privilege) => (
                <li
                  key={privilege.id}
                  style={{
                    display: "flex",
                    columnGap: "10px",
                    alignItems: "center",
                    marginLeft: "-9px",
                    marginBottom: "4px",
                  }}
                >
                  <div className="privilegeToggle">
                    <Switch
                      value={privilege.id}
                      checked={privilege.state}
                      onChange={updatePrivilegeState}
                      color="primary"
                    />
                  </div>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      letterSpacing: "0.15px",
                    }}
                  >
                    {privilege.name}
                  </Typography>
                </li>
              ))}
            </ul>
          </Grid>

          <div
            className="divider"
            style={{ margin: "0px", marginBottom: "24px" }}
          ></div>
        </>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px" }}>
        <Link
          sx={{
            color: "#2B6CB0",
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "22px",
            textDecoration: "underline",
          }}
          onClick={openViewPrivilegesDefinitions}
        >
          View privilege definitions
        </Link>
        <Typography
          style={{
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "22px",
            color: "black",
          }}
        >
          <Link
            sx={{ color: "#2B6CB0", textDecoration: "underline" }}
            onClick={handleResetClick}
          >
            Reset
          </Link>
          &nbsp;this user’s privileges to default values
        </Typography>
        <Typography
          style={{
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "22px",
            color: "black",
          }}
        >
          <Link
            sx={{ color: "#2B6CB0", textDecoration: "underline" }}
            onClick={handleCloneClick}
          >
            Clone
          </Link>
          &nbsp;another user’s privileges and apply to this user
        </Typography>
      </Box>

      {showConfirmResetModal && (
        <ResetPrivileges
          showConfirmResetModal={showConfirmResetModal}
          closeShowConfirmResetModal={closeShowConfirmResetModal}
          resetPrivileges={resetPrivileges}
        />
      )}

      {showSelectCloneModal && (
        <ClonePrivileges
          showSelectCloneModal={showSelectCloneModal}
          closeShowSelectCloneModal={closeShowSelectCloneModal}
          showCloneConfirmationModal={showCloneConfirmationModal}
          selectedUserName={selectedUserName}
          cloneableUsers={cloneableUsers}
          clonePrivileges={clonePrivileges}
          selectedCloneUserId={selectedCloneUserId}
          closeShowCloneConfirmationModal={closeShowCloneConfirmationModal}
          openShowCloneConfirmationModal={openShowCloneConfirmationModal}
          handleUserSelection={handleUserSelection}
        />
      )}

      <PrivilegesDefinition
        open={viewPrivilegesDefinitions}
        onClose={closeViewPrivilegesDefinitions}
      />

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </Grid>
  );
};

export default PrivilegesEdit;
